import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layout, Menu, Progress, Spin } from 'antd';
import { useLocalStorageState } from 'ahooks';
import { styled } from 'styled-components';
import {
  CalendarOutlined,
  ShoppingCartOutlined,
  ScheduleOutlined,
  MailOutlined,
  LikeOutlined,
  BarChartOutlined,
  SettingOutlined,
  ShopOutlined,
  CarryOutOutlined,
  ApartmentOutlined,
  TeamOutlined,
  BlockOutlined,
  UserOutlined,
  UnorderedListOutlined,
  NotificationOutlined,
  CommentOutlined,
  FieldTimeOutlined,
  DashboardOutlined,
  CreditCardOutlined,
  FundProjectionScreenOutlined,
  ToolOutlined,
  EnvironmentOutlined,
  LockOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import useMerchantSettings from '../../hooks/use-merchant-settings';
import {
  isTrialPhasePromoActive,
  isUpsellShorePOSActive,
  isWhitelabelAccount,
} from '../../shared/utils';
import {
  API_ENV,
  BOOKING_BASE_URL,
  POS_MANAGER_BASE_URL,
} from '../../constants';
import { ProgressModal } from './ProgressModal';
import { ProgressCard } from './ProgressCard';
import { SidebarLogo } from './SidebarLogo';
import { changeLocation } from '../../actions';
import { t } from '../../utils/i18n';
import EVENT_BUS_EVENTS from '../../constants/event-bus-events';
import { buildScreenRouteMapping } from '../../constants/screen-routes';
import { useGlobals } from '../../globals-context';
import createQueryConfig from '../../queries';
import { getAppSettings } from '../../selectors';

const { Sider } = Layout;

const StyledSider = styled(Sider)`
  background: white;
  border-right: none;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 100vh;
  border-inline-end: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 3px;
  }

  .ant-layout-sider-trigger {
    display: none;
  }
`;

const StyledMenu = styled(Menu)`
  padding: 5px;
  background: transparent;
  border-inline-end: unset !important;

  .anticon {
    color: rgba(0, 0, 0, 0.45) !important;
  }

  .ant-v5-menu-item {
    height: 48px;
  }

  .menu-item-locked {
    color: ${({ theme }) => theme.antd.colorTextDisabled};

    .anticon {
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }

  .ant-v5-menu-submenu {
    border-radius: unset;
    border-top: 1px solid ${({ theme }) => theme.antd.colorBorderSecondary};

    .ant-v5-menu-submenu-title {
      text-transform: uppercase;
    }
  }
`;

type MenuItem = Required<MenuProps>['items'][number];

interface SideMenuProps {
  collapsed: boolean;
  screenName: string;
}

const getBookingPageUrl = (
  slug: string,
  locale: string,
  hasMultipleLocations: boolean
) => {
  const path = hasMultipleLocations ? 'locations' : 'services';
  return `${BOOKING_BASE_URL}/bookings/${slug}/${path}?locale=${locale}`;
};

const SIDE_MENU_CLICK_EVENT = 'side_menu_item_clicked';

export function SideMenu({ collapsed, screenName }: SideMenuProps) {
  const dispatch = useDispatch();
  const { queryClient, apiClient, trackMethod, eventBus } = useGlobals() as any;
  const queryConfig = createQueryConfig(queryClient, apiClient);
  const { merchantSettings } = useMerchantSettings();
  const { company, trialDaysLeft } = merchantSettings || {};

  const currentMerchant = useSelector((state: any) => state.currentMerchant);
  const currentAccount = useSelector((state: any) => state.currentAccount);
  const accountLocale = currentAccount?.locale || 'en';
  const sessionPermissions = useSelector(
    (state: any) => state.sessionPermissions
  );
  const { subscriptionPartner } =
    useSelector((state: any) => getAppSettings(state)) || {};

  const merchantQuery = currentMerchant?.id
    ? queryConfig.merchantV2(currentMerchant.id)
    : null;

  const { data: merchantData } = useQuery({
    ...merchantQuery,
    enabled: Boolean(merchantQuery),
  }) as any;

  const locationsQuery = queryConfig.locations();
  const { data: locationsData } = useQuery({
    ...locationsQuery,
  }) as any;

  const isTrialUser = isTrialPhasePromoActive({
    companyName: company,
    trialDaysLeft,
    roles: currentAccount?.roles || [],
  });

  const [selectedKey, setSelectedKey] = useState<string>('');
  const [isProgressModalOpen, setProgressModalOpen] = useState(false);

  const [openKeys, setOpenKeys] = useLocalStorageState<string[]>(
    'as:menuOpenKeys',
    {
      defaultValue: [],
      listenStorageChange: false,
    }
  );

  const isInitialized = useRef(false);
  const prevScreenName = useRef('');
  const prevCollapsed = useRef(collapsed);

  const hasMultipleLocations = useMemo(() => {
    if (locationsData?.locations) {
      return locationsData.locations.length > 1;
    }
    return false;
  }, [locationsData]);

  const bookingPageUrl = getBookingPageUrl(
    merchantData?.slug,
    accountLocale,
    hasMultipleLocations
  );
  const isPOSUpsellActive = useMemo(
    () =>
      isUpsellShorePOSActive({
        locale: accountLocale,
        companyName: company,
        packageCodes: currentAccount?.contractDetails?.packages,
      }),
    [accountLocale, company, currentAccount?.contractDetails?.packages]
  );

  const isWhitelabel = useMemo(
    () => isWhitelabelAccount(currentMerchant?.whiteLabel),
    [currentMerchant?.whiteLabel]
  );

  const isReseller = useMemo(
    () => Boolean(subscriptionPartner),
    [subscriptionPartner]
  );
  const screenRouteMapping = useMemo(
    () =>
      buildScreenRouteMapping({
        bookingPageUrl,
        posManagerUrl: POS_MANAGER_BASE_URL,
      }),
    [bookingPageUrl, POS_MANAGER_BASE_URL]
  );

  const isDataLoading = useMemo(
    () => Object.values(sessionPermissions).some((value) => value === null),
    [sessionPermissions]
  );

  const isMarketingMenuVisible = useMemo(
    () =>
      sessionPermissions.messenger !== null &&
      sessionPermissions.newsletter !== null &&
      sessionPermissions.feedback !== null &&
      sessionPermissions.reminders !== null,
    [sessionPermissions]
  );

  const isManagementMenuVisible = useMemo(
    () =>
      sessionPermissions.customers !== null &&
      sessionPermissions.resources !== null &&
      sessionPermissions.hr !== null &&
      sessionPermissions.services !== null,
    [sessionPermissions]
  );

  const isAnalysisMenuVisible = useMemo(
    () =>
      sessionPermissions.insights !== null &&
      sessionPermissions.transactions !== null &&
      sessionPermissions.reportings !== null,
    [sessionPermissions]
  );

  const hasVisibleMarketingItems = useMemo(() => {
    if (!isMarketingMenuVisible) return false;

    const messengerVisible =
      !isTrialUser &&
      !isWhitelabel &&
      !isReseller &&
      sessionPermissions?.messenger?.available;

    const newsletterVisible =
      !isTrialUser &&
      !isWhitelabel &&
      !isReseller &&
      sessionPermissions?.newsletter?.available;

    const feedbackVisible =
      !isWhitelabel && !isReseller && sessionPermissions?.feedback?.available;
    const remindersVisible =
      !isWhitelabel && sessionPermissions?.reminders?.available;

    return (
      messengerVisible ||
      newsletterVisible ||
      feedbackVisible ||
      remindersVisible
    );
  }, [
    isMarketingMenuVisible,
    isTrialUser,
    isWhitelabel,
    isReseller,
    sessionPermissions,
  ]);

  const hasVisibleAnalysisItems = useMemo(() => {
    if (!isAnalysisMenuVisible) return false;

    const insightsVisible = sessionPermissions?.insights?.available;
    const transactionsVisible = sessionPermissions?.transactions?.available;
    const reportingsVisible =
      !isTrialUser &&
      !isWhitelabel &&
      !isReseller &&
      sessionPermissions?.reportings?.available;

    return insightsVisible || transactionsVisible || reportingsVisible;
  }, [
    isAnalysisMenuVisible,
    isTrialUser,
    isWhitelabel,
    isReseller,
    sessionPermissions,
  ]);

  const menuItems: MenuItem[] = useMemo(
    () => [
      ...(sessionPermissions?.calendar?.available
        ? [
            {
              key: '/calendar',
              icon: <CalendarOutlined />,
              label: t('side-menu.calendar'),
            },
          ]
        : []),
      ...(!isWhitelabel && !isReseller && currentAccount?.id !== null
        ? [
            {
              key: isPOSUpsellActive
                ? '/upsell/shorepos'
                : POS_MANAGER_BASE_URL,
              icon: <ShoppingCartOutlined />,
              className: isPOSUpsellActive ? 'menu-item-locked' : '',
              label: (
                <span>
                  {t('side-menu.shore-pos')}
                  {isPOSUpsellActive && (
                    <LockOutlined
                      style={{
                        fontSize: 12,
                        marginLeft: 4,
                        marginBottom: 4,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                </span>
              ),
            },
          ]
        : []),

      ...(merchantData?.slug
        ? [
            {
              key: bookingPageUrl,
              icon: <ShopOutlined />,
              label: <span>{t('side-menu.booking-page')} </span>,
            },
          ]
        : []),
      ...(!isTrialUser && !isWhitelabel && !isReseller
        ? [
            {
              key: sessionPermissions?.shiftplan?.available
                ? '/shiftplan'
                : '/upsell/shiftplan',
              icon: <CarryOutOutlined />,
              className:
                sessionPermissions?.shiftplan?.available === false
                  ? 'menu-item-locked'
                  : '',
              label: (
                <span>
                  {t('side-menu.shift-plan')}
                  {sessionPermissions?.shiftplan?.available === false && (
                    <LockOutlined
                      style={{ fontSize: 12, marginLeft: 4, cursor: 'pointer' }}
                    />
                  )}
                </span>
              ),
            },
          ]
        : []),
      ...(isManagementMenuVisible
        ? [
            {
              key: 'management',
              icon: <ApartmentOutlined />,
              label: t('side-menu.management'),
              children: [
                ...(sessionPermissions?.customers?.available
                  ? [
                      {
                        key: '/customers',
                        icon: <TeamOutlined />,
                        label: t('side-menu.customers'),
                      },
                    ]
                  : []),
                ...(sessionPermissions?.resources?.available
                  ? [
                      {
                        key: '/resources',
                        icon: <BlockOutlined />,
                        label: t('side-menu.resources'),
                      },
                    ]
                  : []),
                ...(sessionPermissions?.hr?.available
                  ? [
                      {
                        key: '/hr',
                        icon: <UserOutlined />,
                        label: t('side-menu.employees'),
                      },
                    ]
                  : []),

                ...(sessionPermissions?.services?.available
                  ? [
                      {
                        key: '/services',
                        icon: <UnorderedListOutlined />,
                        label: t('side-menu.services'),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),

      ...(hasVisibleMarketingItems
        ? [
            {
              key: 'marketing',
              icon: <NotificationOutlined />,
              label: t('side-menu.marketing'),
              children: [
                ...(!isTrialUser && !isWhitelabel && !isReseller
                  ? [
                      {
                        key: sessionPermissions?.messenger?.available
                          ? '/services#/messenger'
                          : '/upsell/messenger',
                        icon: <CommentOutlined />,
                        className:
                          sessionPermissions?.messenger?.available === false
                            ? 'menu-item-locked'
                            : '',
                        label: (
                          <span>
                            {t('side-menu.messenger')}
                            {sessionPermissions?.messenger?.available ===
                              false && (
                              <LockOutlined
                                style={{
                                  fontSize: 12,
                                  marginLeft: 4,
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </span>
                        ),
                      },
                    ]
                  : []),
                ...(!isTrialUser && !isWhitelabel && !isReseller
                  ? [
                      {
                        key: sessionPermissions?.newsletter?.available
                          ? '/newsletter'
                          : '/upsell/newsletter',
                        icon: <MailOutlined />,
                        className:
                          sessionPermissions?.newsletter?.available === false
                            ? 'menu-item-locked'
                            : '',
                        label: (
                          <span>
                            {t('side-menu.newsletter')}
                            {sessionPermissions?.newsletter?.available ===
                              false && (
                              <LockOutlined
                                style={{
                                  fontSize: 12,
                                  marginLeft: 4,
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </span>
                        ),
                      },
                    ]
                  : []),
                ...(!isWhitelabel &&
                !isReseller &&
                sessionPermissions?.feedback?.available
                  ? [
                      {
                        key: '/feedback',
                        icon: <LikeOutlined />,
                        label: t('side-menu.feedback'),
                      },
                    ]
                  : []),
                ...(!isWhitelabel && sessionPermissions?.reminders?.available
                  ? [
                      {
                        key: '/reminders',
                        icon: <FieldTimeOutlined />,
                        label: t('side-menu.reminders'),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...(hasVisibleAnalysisItems
        ? [
            {
              key: 'analysis',
              icon: <BarChartOutlined />,
              label: t('side-menu.analysis'),
              children: [
                ...(sessionPermissions?.insights?.available
                  ? [
                      {
                        key: '/insights',
                        icon: <DashboardOutlined />,
                        label: t('side-menu.dashboard'),
                      },
                    ]
                  : []),
                ...(sessionPermissions?.transactions?.available
                  ? [
                      {
                        key: '/transactions',
                        icon: <CreditCardOutlined />,
                        label: t('side-menu.transactions'),
                      },
                    ]
                  : []),
                ...(!isTrialUser && !isWhitelabel && !isReseller
                  ? [
                      {
                        key: sessionPermissions?.reportings?.available
                          ? '/pos-reports-v2'
                          : '/upsell/reports',
                        icon: <FundProjectionScreenOutlined />,
                        className:
                          sessionPermissions?.reportings?.available === false
                            ? 'menu-item-locked'
                            : '',
                        label: (
                          <span>
                            {t('side-menu.reports')}
                            {sessionPermissions?.reportings?.available ===
                              false && (
                              <LockOutlined
                                style={{
                                  fontSize: 12,
                                  marginLeft: 4,
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                          </span>
                        ),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),

      {
        key: 'setup',
        icon: <SettingOutlined />,
        label: t('side-menu.setup'),
        children: [
          ...(sessionPermissions?.settings?.available
            ? [
                {
                  key: '/application-settings',
                  icon: <ScheduleOutlined />,
                  label: t('side-menu.calendar-settings'),
                },
              ]
            : []),
          ...(sessionPermissions?.settings?.available
            ? [
                {
                  key: '/settings',
                  icon: <ToolOutlined />,
                  label: t('side-menu.company-settings'),
                },
              ]
            : []),
          ...(currentAccount?.roles.length > 1
            ? [
                {
                  key: '/locations',
                  icon: <EnvironmentOutlined />,
                  label: t('side-menu.location-settings'),
                },
              ]
            : []),
        ],
      },
    ],
    [
      sessionPermissions,
      accountLocale,
      bookingPageUrl,
      isPOSUpsellActive,
      isTrialUser,
      isManagementMenuVisible,
      hasVisibleMarketingItems,
      hasVisibleAnalysisItems,
      dispatch,
      eventBus,
      isWhitelabel,
      isReseller,
    ]
  );

  useEffect(() => {
    const mapping = screenRouteMapping[screenName];

    if (!mapping) {
      return;
    }

    if (prevScreenName.current === screenName && isInitialized.current) {
      return;
    }
    prevScreenName.current = screenName;
    const newSelectedKey = mapping.key;

    if (selectedKey !== newSelectedKey) {
      setSelectedKey(newSelectedKey);
    }

    // Update openKeys based on parent, but only if not collapsed
    // Don't re-trigger if only collapsed changed
    if (!collapsed && mapping.parent) {
      const newOpenKeys = [mapping.parent];
      if (JSON.stringify(openKeys) !== JSON.stringify(newOpenKeys)) {
        setOpenKeys(newOpenKeys);
      }
    } else if (collapsed && openKeys && openKeys.length > 0) {
      // Clear openKeys when collapsed
      setOpenKeys([]);
    }

    isInitialized.current = true;
    prevCollapsed.current = collapsed;
    // eslint-disable-next-line
  }, [screenRouteMapping, screenName, collapsed]);

  const handleOpenChange = (keys: string[]) => {
    if (!keys || keys.length === 0) {
      setOpenKeys([]);
      return;
    }
    const latest = keys[keys.length - 1];
    setOpenKeys(latest ? [latest] : []);
  };

  const handleOnMenuClick: MenuProps['onClick'] = (e) => {
    const menuItem = menuItems.find((item: any) => item?.key === e.key);
    if (menuItem && (menuItem as any).disabled) {
      return;
    }

    if (e.key.startsWith('http://') || e.key.startsWith('https://')) {
      window.open(e.key, '_blank');
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key,
      });
      return;
    }

    if (e.key.startsWith('/')) {
      const route = e.key;
      setSelectedKey(e.key);
      dispatch(changeLocation(route));
      trackMethod(SIDE_MENU_CLICK_EVENT, {
        platform: 'booking',
        location: 'side_menu_link',
        menu_item: e.key,
      });
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route } });
    }
  };

  return (
    <StyledSider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={260}
      collapsedWidth={80}
    >
      <SidebarLogo collapsed={collapsed} isLoading={isDataLoading} />
      {isDataLoading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 120px)',
            width: '100%',
            gap: 12,
          }}
        >
          <Spin size="large" />
          <div style={{ color: '#8c8c8c', fontSize: 14 }}>Loading...</div>
        </div>
      ) : (
        <StyledMenu
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={menuItems}
          onClick={handleOnMenuClick}
        />
      )}

      {/* <ProgressCard
        collapsed={collapsed}
        onOpenProgressModal={() => setProgressModalOpen(true)}
      /> */}

      {/* {isProgressModalOpen && (
        <ProgressModal
          open={isProgressModalOpen}
          onClose={() => setProgressModalOpen(false)}
        />
      )} */}
    </StyledSider>
  );
}
